import React from 'react';
import styled from 'styled-components';

import { useTranslate } from '@common/hooks/useTranslate';
import { Anchor, NodeDataType } from '@components/Flow/Nodes';
import { AnchorType } from '@components/Flow';
import { Command } from '@store/slices';
import { isConnectable, isValidConnection } from '../utils';
import { useSelector } from 'react-redux';
import { State } from '@store/index';

export const NodeAnchorsContainer = styled.div<{ extendedSources?: boolean }>`
  display: grid;
  grid-template-columns: ${({ extendedSources }) => (extendedSources ? '1fr 1.75fr' : '1fr 1fr')};

  #label {
    display: flex;
    align-items: center;
    margin-bottom: 0.65rem;
    padding-bottom: 0.65rem;
    border-bottom: 1px dashed ${({ theme }) => theme.line.light};
    gap: 0.5rem;
    width: 100%;

    svg {
      font-size: 11px;
      color: ${({ theme }) => theme.text.subtext};
    }

    p {
      font-size: 11px;
      font-weight: 400;
      color: ${({ theme }) => theme.text.subtext};
    }
  }
`;

export const InputAnchors = styled.div<{ hasInputs?: boolean }>`
  display: flex;
  flex-direction: column;
  border-right: ${({ theme, hasInputs }) => (hasInputs ? `1px solid ${theme.line.light}` : 'none')};
  padding: 0.75rem 1rem;
  min-width: 0;
`;

export const OutputAnchors = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 0.75rem 1rem;
  min-width: 0;
`;

interface Props {
  id: string;
  command: Command;
  data: NodeDataType;
  anchors: AnchorType[];
}

export const NodeAnchors: React.FC<Props> = ({ id, command, data, anchors }) => {
  const connections = command.connections || [];

  const { isInfiniteLoop } = useTranslate();

  const commands = useSelector((state: State) => state.tabs.active.commands);
  const edges = useSelector((state: State) => state.tabs.active.edges);

  const sources = anchors.filter((anchor) => anchor.type === 'source');
  const targets = anchors.filter((anchor) => anchor.type === 'target');

  return (
    <NodeAnchorsContainer>
      <InputAnchors hasInputs={Boolean(targets.length && sources.length)}>
        {targets.map((target) => (
          <Anchor
            key={`${target.id}${id}`}
            id={id}
            data={data}
            anchor={target}
            isValidConnection={(connection) => isValidConnection(connection, commands, edges, isInfiniteLoop)}
            isConnectable={isConnectable(target, connections)}
          />
        ))}
      </InputAnchors>
      <OutputAnchors>
        {sources.map((source) => (
          <Anchor
            key={`${source.id}${id}`}
            id={id}
            data={data}
            anchor={source}
            isValidConnection={(connection) => isValidConnection(connection, commands, edges, isInfiniteLoop)}
            isConnectable={isConnectable(source, connections)}
          />
        ))}
      </OutputAnchors>
    </NodeAnchorsContainer>
  );
};
